import React from 'react';
import { User, Users, ArrowRightLeft, Trophy } from 'lucide-react';
import { MatchplayFormat } from '../../../types/matchplay-v2';

interface FormatSelectorProps {
  selectedFormat: MatchplayFormat;
  onFormatSelect: (format: MatchplayFormat) => void;
}

interface FormatOption {
  value: MatchplayFormat;
  label: string;
  icon: React.ComponentType<any>;
  description: string;
  details: string[];
  playerCount: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
}

export function FormatSelector({ selectedFormat, onFormatSelect }: FormatSelectorProps) {
  const formats: FormatOption[] = [
    {
      value: 'singles',
      label: 'Singles',
      icon: User,
      description: 'Individual head-to-head matches',
      details: [
        'One player vs one player',
        'Each player plays their own ball',
        'Simplest scoring format',
        'Great for beginners'
      ],
      playerCount: '2 players per match',
      difficulty: 'Easy'
    },
    {
      value: 'fourball',
      label: 'Four Ball (Best Ball)',
      icon: Users,
      description: 'Team matches using best ball scoring',
      details: [
        'Two players per team',
        'Each player plays their own ball',
        'Team score is the lower of the two',
        'Popular team format'
      ],
      playerCount: '4 players per match',
      difficulty: 'Medium'
    },
    {
      value: 'foursomes',
      label: 'Foursomes (Alternate Shot)',
      icon: ArrowRightLeft,
      description: 'Team matches with alternating shots',
      details: [
        'Two players per team',
        'Teams alternate hitting the same ball',
        'Most challenging format',
        'Requires teamwork and strategy'
      ],
      playerCount: '4 players per match',
      difficulty: 'Hard'
    }
  ];

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Easy': return 'text-green-600 bg-green-100';
      case 'Medium': return 'text-yellow-600 bg-yellow-100';
      case 'Hard': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Choose Match Format</h2>
        <p className="text-gray-600">Select the format that best suits your tournament style</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {formats.map((format) => {
          const Icon = format.icon;
          const isSelected = selectedFormat === format.value;
          
          return (
            <button
              key={format.value}
              onClick={() => onFormatSelect(format.value)}
              className={`relative p-6 rounded-xl border-2 transition-all duration-200 text-left hover:shadow-lg ${
                isSelected 
                  ? 'border-primary-500 bg-primary-50 shadow-md transform scale-105' 
                  : 'border-gray-200 bg-white hover:border-primary-300'
              }`}
            >
              {/* Selection indicator */}
              {isSelected && (
                <div className="absolute top-4 right-4">
                  <div className="w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center">
                    <Trophy className="w-4 h-4 text-white" />
                  </div>
                </div>
              )}

              {/* Format icon and title */}
              <div className="flex items-center mb-3">
                <div className={`p-3 rounded-lg ${
                  isSelected ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-600'
                }`}>
                  <Icon className="w-6 h-6" />
                </div>
                <div className="ml-3">
                  <h3 className={`text-lg font-semibold ${
                    isSelected ? 'text-primary-700' : 'text-gray-900'
                  }`}>
                    {format.label}
                  </h3>
                </div>
              </div>

              {/* Description */}
              <p className="text-gray-600 mb-4">{format.description}</p>

              {/* Details list */}
              <ul className="space-y-2 mb-4">
                {format.details.map((detail, index) => (
                  <li key={index} className="flex items-start text-sm text-gray-600">
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 mr-2 flex-shrink-0" />
                    {detail}
                  </li>
                ))}
              </ul>

              {/* Format stats */}
              <div className="border-t border-gray-200 pt-4 space-y-2">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-500">Players per match:</span>
                  <span className="font-medium text-gray-700">{format.playerCount}</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-500">Difficulty:</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(format.difficulty)}`}>
                    {format.difficulty}
                  </span>
                </div>
              </div>

              {/* Selection prompt */}
              <div className={`mt-4 text-center text-sm font-medium ${
                isSelected ? 'text-primary-600' : 'text-gray-400'
              }`}>
                {isSelected ? 'Selected Format' : 'Click to Select'}
              </div>
            </button>
          );
        })}
      </div>

      {/* Format comparison table */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Format Comparison</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 font-medium text-gray-700">Format</th>
                <th className="text-left py-2 font-medium text-gray-700">Team Size</th>
                <th className="text-left py-2 font-medium text-gray-700">Scoring Method</th>
                <th className="text-left py-2 font-medium text-gray-700">Best For</th>
              </tr>
            </thead>
            <tbody className="text-gray-600">
              <tr className="border-b border-gray-100">
                <td className="py-2 font-medium">Singles</td>
                <td className="py-2">1 player</td>
                <td className="py-2">Individual score</td>
                <td className="py-2">Individual competition</td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-2 font-medium">Four Ball</td>
                <td className="py-2">2 players</td>
                <td className="py-2">Best ball (lower score)</td>
                <td className="py-2">Team collaboration</td>
              </tr>
              <tr>
                <td className="py-2 font-medium">Foursomes</td>
                <td className="py-2">2 players</td>
                <td className="py-2">Alternate shots</td>
                <td className="py-2">Strategic partnerships</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex justify-between pt-4">
        <button
          onClick={() => window.history.back()}
          className="px-4 py-2 text-gray-600 hover:text-gray-800"
        >
          Back
        </button>
        <button
          onClick={() => onFormatSelect(selectedFormat)}
          className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 flex items-center gap-2"
        >
          Continue with {formats.find(f => f.value === selectedFormat)?.label}
          <ArrowRightLeft className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
} 